import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Zap, Trophy, ArrowLeft, Timer, Target, Loader2, Check, X, Sparkles,
  UserPlus, LogIn, Coins, TrendingUp, Share2, Copy, MessageCircle, Facebook, Instagram,
  Mail, Crown, CloudUpload
} from "lucide-react";

// ==================== Sound ====================
const audioCtx = typeof window !== "undefined" ? new (window.AudioContext || window.webkitAudioContext)() : null;
function beep(freq, dur, type = "sine", gain = 0.25) {
  if (!audioCtx) return;
  if (audioCtx.state === "suspended") audioCtx.resume();
  const osc = audioCtx.createOscillator();
  const g = audioCtx.createGain();
  osc.type = type; osc.frequency.value = freq;
  g.gain.setValueAtTime(gain, audioCtx.currentTime);
  g.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + dur);
  osc.connect(g).connect(audioCtx.destination);
  osc.start(); osc.stop(audioCtx.currentTime + dur);
}
const playCorrect = () => { beep(880, 0.1); setTimeout(() => beep(1320, 0.12), 60); };
const playWrong = () => { beep(200, 0.15, "square"); };

// ==================== Local storage helpers ====================
const LS_KEY = "guestArena";
function loadGuest() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return { nickname: "", coins: 0, bestScore: 0, bestSpeedScore: 0, gamesPlayed: 0, promptedToSignup: false };
    return { ...JSON.parse(raw) };
  } catch (e) {
    return { nickname: "", coins: 0, bestScore: 0, bestSpeedScore: 0, gamesPlayed: 0, promptedToSignup: false };
  }
}
function saveGuest(data) {
  try { localStorage.setItem(LS_KEY, JSON.stringify(data)); } catch (e) { /* noop */ }
}

// ==================== Constants ====================
const PRACTICE_COUNT = 20;
const SPEED_SECONDS = 60;

export default function GuestArenaPage() {
  const navigate = useNavigate();
  const { user, token, login } = useAuth();
  const [guest, setGuest] = useState(loadGuest);
  const [phase, setPhase] = useState("setup"); // setup | playing | results
  const [mode, setMode] = useState("practice"); // practice | speed
  const [selectedTables, setSelectedTables] = useState([2, 3, 4, 5]);
  const [questions, setQuestions] = useState([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [answerInput, setAnswerInput] = useState("");
  const [correctCount, setCorrectCount] = useState(0);
  const [wrongCount, setWrongCount] = useState(0);
  const [streak, setStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);
  const [timeLeft, setTimeLeft] = useState(SPEED_SECONDS);
  const [feedback, setFeedback] = useState(null); // "correct" | "wrong" | null
  const [loading, setLoading] = useState(false);
  const [showSignupPrompt, setShowSignupPrompt] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [showAuthDialog, setShowAuthDialog] = useState(false);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [leaders, setLeaders] = useState(null);
  const [magicEmail, setMagicEmail] = useState("");
  const [magicSending, setMagicSending] = useState(false);
  const [magicSent, setMagicSent] = useState(false);
  const [nicknameInput, setNicknameInput] = useState("");
  const inputRef = useRef(null);
  const startTimeRef = useRef(0);
  const timerRef = useRef(null);
  const authProcessed = useRef(false);

  // ==================== SIGNED-IN PROGRESS SYNC ====================
  const syncProgress = useCallback(async (tok, guestData) => {
    const g = guestData || loadGuest();
    try {
      const r = await axios.post(`${API}/arena/player/sync`, {
        nickname: g.nickname || "",
        coins: g.coins || 0,
        best_practice_score: g.bestScore || 0,
        best_speed_score: g.bestSpeedScore || 0,
        games_played: g.gamesPlayed || 0
      }, { headers: { Authorization: `Bearer ${tok}` } });
      const merged = {
        ...g,
        nickname: r.data.nickname || g.nickname,
        coins: r.data.coins,
        bestScore: r.data.best_practice_score,
        bestSpeedScore: r.data.best_speed_score,
        gamesPlayed: r.data.games_played
      };
      setGuest(merged); saveGuest(merged);
      return merged;
    } catch (e) { return g; }
  }, []);

  // Handle Google (#session_id) and Magic Link (?magic_token) returns
  useEffect(() => {
    if (authProcessed.current) return;
    const hash = window.location.hash || "";
    const magicToken = new URLSearchParams(window.location.search).get("magic_token");
    const finishAuth = async (r) => {
      login(r.data.user, r.data.access_token);
      window.history.replaceState(null, "", window.location.pathname);
      await syncProgress(r.data.access_token);
      toast.success(`Welcome, ${r.data.user.name}! Your Arena progress is now saved to your account.`);
    };
    if (hash.includes("session_id=")) {
      authProcessed.current = true;
      const sessionId = new URLSearchParams(hash.replace("#", "")).get("session_id");
      (async () => {
        try {
          const r = await axios.post(`${API}/arena/auth/session`, { session_id: sessionId });
          await finishAuth(r);
        } catch (e) { toast.error("Google sign-in failed. Please try again."); }
      })();
    } else if (magicToken) {
      authProcessed.current = true;
      (async () => {
        try {
          const r = await axios.post(`${API}/arena/auth/magic-verify`, { token: magicToken });
          await finishAuth(r);
        } catch (e) { toast.error(e.response?.data?.detail || "Magic link expired or invalid. Please request a new one."); }
      })();
    } else if (user && token) {
      // Already signed in — pull server progress and merge
      authProcessed.current = true;
      syncProgress(token);
    }
  }, [user, token, login, syncProgress]);

  const startGoogleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/play/arena";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const sendMagicLink = async () => {
    const email = magicEmail.trim();
    if (!email || !email.includes("@")) { toast.error("Enter a valid email address"); return; }
    setMagicSending(true);
    try {
      await axios.post(`${API}/arena/auth/magic-link`, { email, origin: window.location.origin });
      setMagicSent(true);
      toast.success("Magic link sent! Check your inbox and tap the link to sign in.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't send the link. Please try again.");
    } finally { setMagicSending(false); }
  };

  const openLeaderboard = async () => {
    setShowLeaderboard(true);
    try {
      const r = await axios.get(`${API}/arena/player/leaderboard`);
      setLeaders(r.data.leaderboard || []);
    } catch (e) { setLeaders([]); }
  };

  // If signed-in user reaches here, offer to go to real arena
  useEffect(() => {
    if (user && phase === "setup") {
      // Show a suggestion but don't force
      // (they might genuinely want guest mode)
    }
  }, [user, phase]);

  const toggleTable = (t) => {
    setSelectedTables(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t].sort((a, b) => a - b));
  };

  const startGame = async () => {
    if (selectedTables.length === 0) { toast.error("Pick at least one times table"); return; }
    if (!guest.nickname && !nicknameInput.trim()) {
      // require a nickname first-run only (or if they left it blank)
      // We'll just default to "Guest"
    }
    setLoading(true);
    try {
      const count = mode === "practice" ? PRACTICE_COUNT : 50;
      const res = await axios.post(`${API}/arena/guest/generate`, { tables: selectedTables, count });
      setQuestions(res.data.questions);
      setCurrentIdx(0);
      setAnswerInput("");
      setCorrectCount(0);
      setWrongCount(0);
      setStreak(0);
      setBestStreak(0);
      setTimeLeft(SPEED_SECONDS);
      setFeedback(null);
      setPhase("playing");
      startTimeRef.current = Date.now();
      setTimeout(() => inputRef.current?.focus(), 100);

      // Save nickname if entered
      if (nicknameInput.trim() && !guest.nickname) {
        const next = { ...guest, nickname: nicknameInput.trim() };
        setGuest(next); saveGuest(next);
      }
    } catch (e) {
      toast.error("Couldn't start game. Please try again.");
    } finally { setLoading(false); }
  };

  // Timer for speed mode
  useEffect(() => {
    if (phase !== "playing" || mode !== "speed") return;
    timerRef.current = setInterval(() => {
      setTimeLeft(t => {
        if (t <= 1) {
          clearInterval(timerRef.current);
          finishGame();
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [phase, mode]);

  const submitAnswer = () => {
    if (!answerInput.trim() || currentIdx >= questions.length) return;
    const q = questions[currentIdx];
    const isCorrect = Number(answerInput) === q.answer;
    if (isCorrect) {
      playCorrect();
      setCorrectCount(c => c + 1);
      setStreak(s => {
        const next = s + 1;
        setBestStreak(bs => Math.max(bs, next));
        return next;
      });
      setFeedback("correct");
    } else {
      playWrong();
      setWrongCount(w => w + 1);
      setStreak(0);
      setFeedback("wrong");
    }
    setTimeout(() => setFeedback(null), 400);
    setAnswerInput("");
    const nextIdx = currentIdx + 1;
    if (mode === "practice" && nextIdx >= questions.length) {
      finishGame(nextIdx, isCorrect);
      return;
    }
    if (mode === "speed" && nextIdx >= questions.length) {
      // Regenerate more questions on the fly for speed mode
      loadMoreForSpeed();
    }
    setCurrentIdx(nextIdx);
    setTimeout(() => inputRef.current?.focus(), 20);
  };

  const loadMoreForSpeed = async () => {
    try {
      const res = await axios.post(`${API}/arena/guest/generate`, { tables: selectedTables, count: 50 });
      setQuestions(prev => [...prev, ...res.data.questions.map((q, i) => ({ ...q, index: prev.length + i }))]);
    } catch (e) { /* silent */ }
  };

  const finishGame = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    const coinsEarned = correctCount + Math.max(0, bestStreak - 2);
    const isSpeed = mode === "speed";
    const scoreToSave = correctCount;
    const key = isSpeed ? "bestSpeedScore" : "bestScore";
    const next = {
      ...guest,
      coins: guest.coins + coinsEarned,
      gamesPlayed: guest.gamesPlayed + 1,
      [key]: Math.max(guest[key] || 0, scoreToSave)
    };
    setGuest(next); saveGuest(next);
    setPhase("results");

    // Auto-sync progress to the server for signed-in players
    if (user && token) {
      syncProgress(token, next);
    }

    // Sign-up nudge after 3 games (once)
    if (next.gamesPlayed >= 3 && !next.promptedToSignup && !user) {
      setShowSignupPrompt(true);
      const nnext = { ...next, promptedToSignup: true };
      setGuest(nnext); saveGuest(nnext);
    }
  }, [correctCount, bestStreak, mode, guest, user, token, syncProgress]);

  const currentQ = questions[currentIdx];
  const total = correctCount + wrongCount;
  const accuracy = total > 0 ? Math.round((correctCount / total) * 100) : 0;

  // ==================== SHARE HELPERS ====================
  const shareUrl = typeof window !== "undefined" ? `${window.location.origin}/play/arena` : "https://app.themathemind.com/play/arena";
  const shareText = (() => {
    const who = guest.nickname ? guest.nickname : "I";
    const modeLabel = mode === "speed" ? `in 60s (Speed Challenge)` : `on Practice Mode`;
    return `${who} just scored ${correctCount} correct answers ${modeLabel} on TheMatheMind 360 Times Tables Arena ⚡ (${accuracy}% accuracy, best streak ${bestStreak}). Beat me at ${shareUrl}`;
  })();

  const shareToWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, "_blank", "noopener,noreferrer");
  };
  const shareToFacebook = () => {
    // Facebook picks up the page's OG tags; quote param appends the user's text
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`, "_blank", "noopener,noreferrer,width=600,height=500");
  };
  const shareToInstagram = async () => {
    // Instagram doesn't accept URL prefill — copy caption then open IG
    try {
      await navigator.clipboard.writeText(shareText);
      toast.success("Caption copied! Paste it into your Instagram story or post.");
      setTimeout(() => window.open("https://www.instagram.com/", "_blank", "noopener,noreferrer"), 500);
    } catch (e) {
      toast.error("Couldn't copy caption. Please copy the text manually.");
    }
  };
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareText);
      toast.success("Copied! Paste anywhere.");
    } catch (e) {
      toast.error("Couldn't copy. Please copy manually.");
    }
  };
  const tryNativeShare = async () => {
    if (!navigator.share) { setShowShareDialog(true); return; }
    try {
      await navigator.share({ title: "TheMatheMind 360 — Times Tables Arena", text: shareText, url: shareUrl });
    } catch (e) {
      // User cancelled or share failed — fall back to dialog
      if (e?.name !== "AbortError") setShowShareDialog(true);
    }
  };

  // ==================== SHARED DIALOGS (auth + leaderboard) ====================
  const authDialog = (
    <Dialog open={showAuthDialog} onOpenChange={(o) => { setShowAuthDialog(o); if (!o) setMagicSent(false); }}>
      <DialogContent className="max-w-sm" data-testid="arena-auth-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><CloudUpload className="w-5 h-5 text-indigo-500" />Save Your Progress</DialogTitle>
          <DialogDescription>
            Sign in to keep your coins, best scores and streaks forever — across any device — and join the global Arena leaderboard.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Button onClick={startGoogleSignIn} className="w-full h-11 bg-white hover:bg-slate-100 text-slate-800 border border-slate-300 font-semibold" data-testid="google-signin-btn">
            <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.06H2.18A10.97 10.97 0 0 0 1 12c0 1.77.43 3.45 1.18 4.94l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.61 0 3.06.55 4.21 1.64l3.16-3.16C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"/></svg>
            Continue with Google
          </Button>
          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-slate-200" />
            <span className="text-[10px] uppercase text-slate-400">or</span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>
          {magicSent ? (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm text-emerald-800 text-center" data-testid="magic-sent-msg">
              <Mail className="w-5 h-5 mx-auto mb-1" />
              Link sent to <strong>{magicEmail}</strong>! Check your inbox and tap it to sign in (valid 15 min).
            </div>
          ) : (
            <div className="space-y-2">
              <input
                type="email"
                value={magicEmail}
                onChange={(e) => setMagicEmail(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") sendMagicLink(); }}
                placeholder="you@email.com"
                className="w-full px-3 py-2.5 rounded-lg border border-slate-300 text-sm focus:border-indigo-400 focus:outline-none"
                data-testid="magic-email-input"
              />
              <Button onClick={sendMagicLink} disabled={magicSending} className="w-full h-10 bg-indigo-600 hover:bg-indigo-500 text-white font-semibold" data-testid="send-magic-link-btn">
                {magicSending ? <Loader2 className="w-4 h-4 animate-spin" /> : (<><Mail className="w-4 h-4 mr-2" />Email Me a Magic Link</>)}
              </Button>
            </div>
          )}
          <p className="text-[10px] text-slate-400 text-center">Enrolled student or teacher? <button className="underline" onClick={() => navigate("/login")}>Use the regular login</button></p>
        </div>
      </DialogContent>
    </Dialog>
  );

  const leaderboardDialog = (
    <Dialog open={showLeaderboard} onOpenChange={setShowLeaderboard}>
      <DialogContent className="max-w-md" data-testid="player-leaderboard-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Crown className="w-5 h-5 text-yellow-500" />Arena Player Leaderboard</DialogTitle>
          <DialogDescription>Signed-in players ranked by best Speed Challenge score.</DialogDescription>
        </DialogHeader>
        <div className="max-h-80 overflow-y-auto">
          {leaders === null ? (
            <div className="py-8 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" /></div>
          ) : leaders.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-8">No players yet — sign in and set the first record!</p>
          ) : (
            <div className="space-y-1.5">
              {leaders.map((p, i) => (
                <div key={i} className={`flex items-center gap-3 p-2.5 rounded-lg ${i === 0 ? "bg-yellow-50 border border-yellow-200" : "bg-slate-50"}`} data-testid={`leader-row-${i}`}>
                  <span className={`w-6 text-center font-bold text-sm ${i === 0 ? "text-yellow-600" : "text-slate-500"}`}>{i === 0 ? <Crown className="w-4 h-4 mx-auto" /> : i + 1}</span>
                  <span className="flex-1 text-sm font-medium truncate">{p.nickname || p.name || "Player"}</span>
                  <span className="text-xs text-cyan-600 font-bold">{p.best_speed_score} ⚡</span>
                  <span className="text-xs text-amber-600 font-semibold">{p.best_practice_score} 🎯</span>
                  <span className="text-xs text-yellow-600 flex items-center gap-0.5"><Coins className="w-3 h-3" />{p.coins}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );

  // ==================== SETUP PHASE ====================
  if (phase === "setup") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-950 via-slate-950 to-purple-950 text-white" data-testid="guest-arena">
        <div className="border-b border-slate-800/70 backdrop-blur-sm bg-slate-950/50">
          <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => navigate("/")} className="text-white hover:bg-slate-800">
                <ArrowLeft className="w-4 h-4 mr-1" />Home
              </Button>
              <div className="flex items-center gap-2">
                <Zap className="w-6 h-6 text-yellow-400" />
                <div>
                  <h1 className="text-lg font-bold">Times Tables Arena</h1>
                  <p className="text-[10px] text-slate-400">{user ? "Progress synced to your account" : "Play free — no signup needed"}</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="ghost" onClick={openLeaderboard} className="text-white hover:bg-slate-800 text-xs" data-testid="player-leaderboard-btn">
                <Crown className="w-3.5 h-3.5 mr-1 text-yellow-400" />Leaderboard
              </Button>
              {user ? (
                <div className="flex items-center gap-1.5 bg-emerald-900/50 border border-emerald-600/40 rounded-full px-3 py-1.5" data-testid="signed-in-chip">
                  <CloudUpload className="w-3.5 h-3.5 text-emerald-400" />
                  <span className="text-xs text-emerald-200 max-w-[120px] truncate">{user.name}</span>
                </div>
              ) : (
                <Button size="sm" onClick={() => setShowAuthDialog(true)} className="bg-white text-slate-900 hover:bg-slate-100 text-xs font-semibold" data-testid="guest-signin-btn">
                  <LogIn className="w-3.5 h-3.5 mr-1" />Save Progress
                </Button>
              )}
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto p-4 md:p-6">
          {/* Guest stats banner */}
          {guest.gamesPlayed > 0 && (
            <div className="grid grid-cols-3 gap-3 mb-6">
              <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-3 text-center">
                <Coins className="w-4 h-4 mx-auto text-yellow-400 mb-1" />
                <div className="text-lg font-bold text-yellow-400">{guest.coins}</div>
                <div className="text-[10px] text-slate-400 uppercase">Coins</div>
              </div>
              <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-3 text-center">
                <Trophy className="w-4 h-4 mx-auto text-amber-400 mb-1" />
                <div className="text-lg font-bold text-amber-400">{guest.bestScore || 0}</div>
                <div className="text-[10px] text-slate-400 uppercase">Best (Practice)</div>
              </div>
              <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-3 text-center">
                <TrendingUp className="w-4 h-4 mx-auto text-cyan-400 mb-1" />
                <div className="text-lg font-bold text-cyan-400">{guest.bestSpeedScore || 0}</div>
                <div className="text-[10px] text-slate-400 uppercase">Best (Speed)</div>
              </div>
            </div>
          )}

          {/* Nickname */}
          {!guest.nickname && (
            <Card className="mb-4 p-4 bg-slate-900/60 border-slate-800 text-white">
              <label className="text-xs font-semibold uppercase text-slate-300 mb-2 block">Pick a nickname (optional)</label>
              <input
                type="text"
                value={nicknameInput}
                onChange={(e) => setNicknameInput(e.target.value.slice(0, 20))}
                placeholder="e.g. Ninja42"
                className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm focus:border-yellow-400 focus:outline-none"
                data-testid="guest-nickname-input"
              />
            </Card>
          )}

          {/* Mode Select */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
            <button
              onClick={() => setMode("practice")}
              data-testid="mode-practice"
              className={`text-left p-4 rounded-2xl border transition-all ${mode === "practice" ? "bg-gradient-to-br from-emerald-600 to-teal-600 border-emerald-400 shadow-lg" : "bg-slate-900/60 border-slate-800 hover:border-slate-600"}`}
            >
              <div className="flex items-center justify-between mb-2">
                <Target className="w-5 h-5" />
                {mode === "practice" && <Check className="w-4 h-4" />}
              </div>
              <div className="font-bold text-sm">Practice Mode</div>
              <div className="text-[11px] opacity-80 mt-1">{PRACTICE_COUNT} questions · no timer · build your skills</div>
            </button>
            <button
              onClick={() => setMode("speed")}
              data-testid="mode-speed"
              className={`text-left p-4 rounded-2xl border transition-all ${mode === "speed" ? "bg-gradient-to-br from-fuchsia-600 to-pink-600 border-fuchsia-400 shadow-lg" : "bg-slate-900/60 border-slate-800 hover:border-slate-600"}`}
            >
              <div className="flex items-center justify-between mb-2">
                <Zap className="w-5 h-5" />
                {mode === "speed" && <Check className="w-4 h-4" />}
              </div>
              <div className="font-bold text-sm">Speed Challenge</div>
              <div className="text-[11px] opacity-80 mt-1">{SPEED_SECONDS} seconds · beat your record · adrenaline mode</div>
            </button>
          </div>

          {/* Table picker */}
          <Card className="p-4 bg-slate-900/60 border-slate-800 text-white mb-4">
            <div className="text-xs font-semibold uppercase text-slate-300 mb-2">Pick Your Tables ({selectedTables.length} selected)</div>
            <div className="grid grid-cols-5 md:grid-cols-10 gap-2">
              {[2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20].map(t => (
                <button
                  key={t}
                  onClick={() => toggleTable(t)}
                  data-testid={`table-${t}`}
                  className={`aspect-square rounded-lg text-sm font-bold transition-all ${selectedTables.includes(t) ? "bg-yellow-400 text-slate-900 scale-105 shadow-md" : "bg-slate-800 text-slate-400 hover:bg-slate-700"}`}
                >
                  {t}×
                </button>
              ))}
            </div>
            <div className="flex gap-2 mt-3 flex-wrap">
              <Button size="sm" variant="outline" onClick={() => setSelectedTables([2, 3, 4, 5])} className="text-xs bg-slate-800 border-slate-700 hover:bg-slate-700 text-white">Easy (2-5)</Button>
              <Button size="sm" variant="outline" onClick={() => setSelectedTables([6, 7, 8, 9])} className="text-xs bg-slate-800 border-slate-700 hover:bg-slate-700 text-white">Medium (6-9)</Button>
              <Button size="sm" variant="outline" onClick={() => setSelectedTables([10, 11, 12])} className="text-xs bg-slate-800 border-slate-700 hover:bg-slate-700 text-white">Hard (10-12)</Button>
              <Button size="sm" variant="outline" onClick={() => setSelectedTables([13, 14, 15, 16])} className="text-xs bg-slate-800 border-slate-700 hover:bg-slate-700 text-white">Expert (13-16)</Button>
              <Button size="sm" variant="outline" onClick={() => setSelectedTables([17, 18, 19, 20])} className="text-xs bg-slate-800 border-slate-700 hover:bg-slate-700 text-white">Master (17-20)</Button>
              <Button size="sm" variant="outline" onClick={() => setSelectedTables([2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20])} className="text-xs bg-slate-800 border-slate-700 hover:bg-slate-700 text-white">All</Button>
            </div>
          </Card>

          <Button
            onClick={startGame}
            disabled={loading || selectedTables.length === 0}
            className="w-full h-14 rounded-2xl bg-gradient-to-r from-yellow-400 to-amber-500 hover:from-yellow-300 hover:to-amber-400 text-slate-900 font-bold text-base shadow-xl"
            data-testid="guest-start-btn"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (<><Sparkles className="w-5 h-5 mr-2" />Play Now</>)}
          </Button>

          {!user && guest.gamesPlayed >= 2 && (
            <div className="mt-6 bg-gradient-to-r from-indigo-800/60 to-purple-800/60 border border-purple-500/40 rounded-2xl p-4">
              <div className="flex items-start gap-3">
                <UserPlus className="w-5 h-5 text-purple-300 shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="text-sm font-bold">Save your progress + compete</div>
                  <p className="text-xs text-purple-200/80 mt-0.5">Sign in with Google or a magic link to save coins across devices and appear on the Arena player leaderboard.</p>
                </div>
                <Button size="sm" onClick={() => setShowAuthDialog(true)} className="bg-white text-slate-900 hover:bg-slate-100" data-testid="guest-signup-cta">
                  Sign In
                </Button>
              </div>
            </div>
          )}
        </div>
        {authDialog}
        {leaderboardDialog}
      </div>
    );
  }

  // ==================== PLAYING PHASE ====================
  if (phase === "playing") {
    return (
      <div className="min-h-[100dvh] bg-gradient-to-br from-indigo-950 via-slate-950 to-purple-950 text-white flex flex-col" data-testid="guest-arena-playing">
        {/* Top bar */}
        <div className="border-b border-slate-800/70 backdrop-blur-sm bg-slate-950/50 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Zap className="w-5 h-5 text-yellow-400" />
            <div className="text-xs">
              <div className="font-semibold">{guest.nickname || "Guest"}</div>
              <div className="text-slate-400 text-[10px]">Q {currentIdx + 1} · {mode === "practice" ? `of ${PRACTICE_COUNT}` : "Speed"}</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {mode === "speed" && (
              <div className={`px-3 py-1 rounded-full text-sm font-mono font-bold ${timeLeft <= 10 ? "bg-red-500 animate-pulse" : "bg-slate-800"}`} data-testid="guest-timer">
                <Timer className="w-3.5 h-3.5 inline mr-1" />{timeLeft}s
              </div>
            )}
            <div className="text-xs">
              <span className="text-green-400 font-bold" data-testid="guest-correct-count">{correctCount}</span>
              <span className="text-slate-500 mx-1">/</span>
              <span className="text-red-400 font-bold">{wrongCount}</span>
            </div>
            {streak >= 3 && <div className="text-xs px-2 py-0.5 bg-orange-500/80 rounded-full font-bold">🔥 {streak}</div>}
          </div>
        </div>

        {/* Question */}
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="w-full max-w-md">
            <div className={`bg-slate-900/70 border-2 rounded-3xl p-8 md:p-12 text-center transition-colors ${feedback === "correct" ? "border-green-400" : feedback === "wrong" ? "border-red-400" : "border-slate-800"}`}>
              <div className="text-[10px] uppercase text-slate-400 mb-2">What is</div>
              <div className="text-4xl md:text-6xl font-black tracking-tight mb-6" data-testid="guest-question">
                {currentQ ? currentQ.display : "…"}
              </div>
              <input
                ref={inputRef}
                type="number"
                inputMode="numeric"
                autoFocus
                value={answerInput}
                onChange={(e) => setAnswerInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") submitAnswer(); }}
                placeholder="?"
                className={`w-full text-center text-4xl font-bold py-3 rounded-xl bg-slate-800 border-2 text-white focus:outline-none ${feedback === "correct" ? "border-green-400" : feedback === "wrong" ? "border-red-400" : "border-slate-700 focus:border-yellow-400"}`}
                data-testid="guest-answer-input"
              />
              <Button
                onClick={submitAnswer}
                disabled={!answerInput.trim()}
                className="w-full mt-4 h-12 rounded-xl bg-yellow-400 hover:bg-yellow-300 text-slate-900 font-bold"
                data-testid="guest-submit-answer"
              >
                Submit
              </Button>
            </div>

            {mode === "practice" && (
              <Button variant="outline" size="sm" onClick={() => finishGame()} className="w-full mt-3 bg-slate-800/60 border-slate-700 hover:bg-slate-700 text-white text-xs" data-testid="guest-end-early-btn">
                End & See Results
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ==================== RESULTS PHASE ====================
  const coinsEarned = correctCount + Math.max(0, bestStreak - 2);
  const isNewBest = mode === "practice" ? correctCount >= (loadGuest().bestScore || 0) : correctCount >= (loadGuest().bestSpeedScore || 0);
  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-950 via-slate-950 to-purple-950 text-white flex items-center justify-center p-4" data-testid="guest-arena-results">
      <div className="w-full max-w-md">
        <Card className="bg-slate-900/80 border-slate-800 text-white p-6 text-center">
          <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-gradient-to-br from-yellow-400 to-amber-500 flex items-center justify-center">
            <Trophy className="w-8 h-8 text-slate-900" />
          </div>
          <h2 className="text-2xl font-black mb-1">{isNewBest ? "New Best!" : "Nice Try!"}</h2>
          <p className="text-xs text-slate-400 mb-6">{mode === "practice" ? "Practice complete" : "Time's up!"}</p>

          <div className="grid grid-cols-3 gap-2 mb-6">
            <div className="bg-slate-800/70 rounded-xl p-3">
              <div className="text-2xl font-bold text-green-400" data-testid="result-correct">{correctCount}</div>
              <div className="text-[10px] uppercase text-slate-400">Correct</div>
            </div>
            <div className="bg-slate-800/70 rounded-xl p-3">
              <div className="text-2xl font-bold text-cyan-400" data-testid="result-accuracy">{accuracy}%</div>
              <div className="text-[10px] uppercase text-slate-400">Accuracy</div>
            </div>
            <div className="bg-slate-800/70 rounded-xl p-3">
              <div className="text-2xl font-bold text-orange-400" data-testid="result-streak">{bestStreak}</div>
              <div className="text-[10px] uppercase text-slate-400">Best Streak</div>
            </div>
          </div>

          <div className="bg-yellow-400/10 border border-yellow-400/30 rounded-xl p-3 mb-4">
            <div className="flex items-center justify-center gap-2 text-yellow-300">
              <Coins className="w-5 h-5" />
              <span className="font-bold text-lg">+{coinsEarned} coins earned</span>
            </div>
          </div>

          {/* Share button — prominent so friends see the challenge */}
          <Button
            onClick={tryNativeShare}
            className="w-full mb-3 h-11 rounded-xl bg-gradient-to-r from-fuchsia-500 to-purple-600 hover:from-fuchsia-400 hover:to-purple-500 text-white font-semibold shadow-md"
            data-testid="guest-share-btn"
          >
            <Share2 className="w-4 h-4 mr-2" />Share My Score
          </Button>

          <div className="flex gap-2">
            <Button onClick={() => setPhase("setup")} variant="outline" className="flex-1 bg-slate-800 border-slate-700 hover:bg-slate-700 text-white" data-testid="guest-play-again">
              Play Again
            </Button>
            {!user && (
              <Button onClick={() => setShowAuthDialog(true)} className="flex-1 bg-white text-slate-900 hover:bg-slate-100 font-semibold" data-testid="guest-signup-from-results">
                <UserPlus className="w-4 h-4 mr-1" />Save Progress
              </Button>
            )}
          </div>
        </Card>
      </div>

      {/* Share dialog — shown when Web Share API unavailable or user cancels */}
      <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
        <DialogContent className="max-w-sm" data-testid="share-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Share2 className="w-5 h-5 text-purple-500" />Challenge Your Friends</DialogTitle>
            <DialogDescription>
              Share your score and dare your friends to beat it.
            </DialogDescription>
          </DialogHeader>
          <div className="bg-slate-100 border border-slate-200 rounded-lg p-3 text-xs text-slate-700 mb-2 max-h-24 overflow-y-auto">
            {shareText}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Button onClick={shareToWhatsApp} className="h-11 bg-[#25D366] hover:bg-[#1EA952] text-white font-semibold" data-testid="share-whatsapp">
              <MessageCircle className="w-4 h-4 mr-2" />WhatsApp
            </Button>
            <Button onClick={shareToFacebook} className="h-11 bg-[#1877F2] hover:bg-[#155EC1] text-white font-semibold" data-testid="share-facebook">
              <Facebook className="w-4 h-4 mr-2" />Facebook
            </Button>
            <Button onClick={shareToInstagram} className="h-11 bg-gradient-to-br from-[#F58529] via-[#DD2A7B] to-[#8134AF] hover:opacity-90 text-white font-semibold" data-testid="share-instagram">
              <Instagram className="w-4 h-4 mr-2" />Instagram
            </Button>
            <Button onClick={copyLink} variant="outline" className="h-11 font-semibold" data-testid="share-copy">
              <Copy className="w-4 h-4 mr-2" />Copy Link
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Signup nudge (fires once after 3 games) */}
      <Dialog open={showSignupPrompt} onOpenChange={setShowSignupPrompt}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-yellow-500" />You&apos;re on fire!</DialogTitle>
            <DialogDescription>
              You&apos;ve played {guest.gamesPlayed} games. Sign in with Google or a magic link to save your coins across devices and appear on the Arena player leaderboard.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSignupPrompt(false)}>Maybe Later</Button>
            <Button onClick={() => { setShowSignupPrompt(false); setShowAuthDialog(true); }} className="bg-yellow-400 hover:bg-yellow-300 text-slate-900" data-testid="signup-nudge-cta">
              <UserPlus className="w-4 h-4 mr-1" />Save My Progress
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {authDialog}
      {leaderboardDialog}
    </div>
  );
}
